import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { fmtWh } from '../time';

// Settings → HRX schedule (2026-11-05; backend/hrx_schedule.py — part 1 of two). Fernando: "If in the settings there would
// be HRX schedule I could always check is the future correct or is it wrong … it can only build as far as it has data."
// One row per day as far as HRX's e-mails reach, then a week of assumed days: a day from an HRX e-mail carries
// "✓ verified · HRX e-mail 04.11"; a day no e-mail covers is assumed a normal departure (Bring ready 16:00) and has no
// mark. A line the WMS did not recognise is shown with its text and a manager says what it means. Below: the day types'
// times beside HRX's own rules paragraph, the e-mails read (open one to compare), paste one, the mailbox switch.

const card = 'bg-[#1a1d27] rounded-xl border border-gray-800 p-5';
const head = 'text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3';
const input = 'px-2 py-1 rounded bg-[#0f1117] border border-gray-700 text-white text-sm';
const day = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'numeric' });
const dm = (iso: string) => { const [, m, d] = iso.split('-'); return `${d}.${m}`; };

interface Day {
  date: string; type: string; text: string; ready: string | null; order_by: string | null; ship_time: string | null;
  terminal_time: string | null; verified: boolean; source: string; source_text: string; raw: string | null; status: string;
  email: { id: number; subject: string; received_local: string | null } | null; confirmed_by: string | null;
}

export default function HrxScheduleSettings() {
  const [data, setData] = useState<any>(null);
  const [times, setTimes] = useState<any>(null);
  const [assumed, setAssumed] = useState('normal');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [paste, setPaste] = useState('');
  const [subject, setSubject] = useState('');
  const [open, setOpen] = useState<any>(null);
  const load = () => authFetch(`${API}/hrx/schedule`).then(async r => {
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setData(d); setTimes(d.settings.day_types); setAssumed(d.settings.default_when_missing || 'normal');
  });
  useEffect(() => { load(); }, []);
  if (!data || !times) return <div className="text-gray-400 text-sm">{err || 'Loading…'}</div>;

  const types: Record<string, string> = data.types || {};
  const edit = !!data.can_edit;
  const put = async (body: any, done: string) => {
    setErr(''); setMsg('');
    const r = await authFetch(`${API}/hrx/schedule/settings`, { method: 'PUT', body: JSON.stringify(body) });
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setMsg(done); load();
  };
  const confirm = async (d: Day, t: string) => {
    const r = await authFetch(`${API}/hrx/days/${d.date}/confirm`, { method: 'POST', body: JSON.stringify({ day_type: t, email_id: d.email?.id }) });
    if (!r.ok) { const j = await r.json().catch(() => ({} as any)); setErr(j.detail || `HTTP ${r.status}`); return; }
    load();
  };
  const sendPaste = async () => {
    setErr(''); setMsg('');
    const r = await authFetch(`${API}/hrx/emails/paste`, { method: 'POST', body: JSON.stringify({ subject, body: paste }) });
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setMsg(`✓ Read: ${d.days.length} day(s), ${d.status}`); setPaste(''); setSubject(''); load();
  };
  const show = async (id: number) => {
    if (open?.id === id) { setOpen(null); return; }
    const r = await authFetch(`${API}/hrx/emails/${id}`);
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setOpen(d);
  };
  const lp = data.last_pass;
  const rules = data.rules?.rules || {};

  return (
    <div className="space-y-6">
      {msg && <div className="text-green-400 text-sm">{msg}</div>}
      {err && <div className="text-red-400 text-sm">{err}</div>}

      <div className={card}>
        <div className={head}>HRX schedule</div>
        <div className="text-white text-lg font-semibold">
          {data.covered_until ? <>HRX's e-mails reach <b>{day(data.covered_until)}</b></> : "No HRX e-mail read yet — every day is assumed"}</div>
        <div className="text-gray-400 text-sm mb-4">A day with ✓ came from an HRX e-mail. A day no e-mail covers is assumed a{' '}
          {types[assumed] || assumed} (Bring ready by {times[assumed]?.ready || '16:00'}) and has no ✓.</div>
        <div className="space-y-1 text-sm">
          {data.days.map((d: Day) => {
            const isToday = d.date === data.today;
            return (
              <div key={d.date} className={`flex flex-wrap items-center gap-x-4 gap-y-1 px-2 py-1 rounded ${isToday ? 'bg-blue-950/40 border border-blue-900' : ''}`}>
                <span className={`w-28 ${isToday ? 'text-white font-semibold' : 'text-gray-300'}`}>{day(d.date)}{isToday ? ' · today' : ''}</span>
                <span className={`w-56 ${d.type === 'unknown' ? 'text-amber-300' : d.type === 'no_ship' ? 'text-gray-500' : d.verified ? 'text-gray-100' : 'text-gray-400'}`}
                  title={d.raw || ''}>
                  {d.text}{d.ship_time ? <span className="text-gray-500"> · ship {d.ship_time}{d.terminal_time ? `, Tabasalu ${d.terminal_time}` : ''}</span> : null}</span>
                <span className="w-56 text-gray-300">{d.type === 'no_ship' ? '' : <>Bring ready by <b>{d.ready || '—'}</b>{d.order_by ? <> · book HRX by <b>{d.order_by}</b></> : null}</>}</span>
                {d.verified
                  ? <span className="text-xs text-emerald-400" title={d.email?.subject || ''}>✓ verified · {d.source_text}</span>
                  : d.type === 'unknown'
                    ? <span className="flex flex-wrap items-center gap-2 text-xs text-amber-300">needs checking — “{d.raw}”
                        {edit && (
                          <select defaultValue="" onChange={e => e.target.value && confirm(d, e.target.value)} className={input}>
                            <option value="">this means…</option>
                            {['normal', 'via_finland', 'tallinn_late'].map(t => <option key={t} value={t}>{types[t]}</option>)}
                          </select>
                        )}</span>
                    : <span className="text-xs text-gray-500">assumed — no HRX e-mail covers this day</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div className={card}>
        <div className={head}>The day types' times</div>
        <div className="space-y-1 text-sm text-gray-300">
          {Object.keys(times).map(t => (
            <div key={t} className="flex flex-wrap items-center gap-3">
              <span className="w-56">{types[t] || t}</span>
              <label>Bring ready by <input value={times[t].ready || ''} disabled={!edit}
                onChange={e => setTimes({ ...times, [t]: { ...times[t], ready: e.target.value } })} className={`${input} keep-width w-16 ml-1`} /></label>
              <label>book HRX by <input value={times[t].order_by || ''} disabled={!edit} placeholder="—"
                onChange={e => setTimes({ ...times, [t]: { ...times[t], order_by: e.target.value || null } })} className={`${input} keep-width w-16 ml-1`} /></label>
              {rules[t] && (rules[t].ready !== times[t].ready || rules[t].order_by !== times[t].order_by) && (
                <span className="text-xs text-amber-300">the e-mail says ready {rules[t].ready_from}–{rules[t].ready}, book by {rules[t].order_by}</span>)}
            </div>
          ))}
          <label className="block mt-2">A day no e-mail covers is assumed
            <select value={assumed} disabled={!edit} onChange={e => setAssumed(e.target.value)} className={`${input} ml-2`}>
              {['normal', 'via_finland', 'tallinn_late'].map(t => <option key={t} value={t}>{types[t] || t}</option>)}
            </select></label>
        </div>
        {data.rules?.rules_text && (
          <div className="mt-3 text-xs text-gray-500">HRX's rules (e-mail of {fmtWh(data.rules.received_at, 'date')}): {data.rules.rules_text}</div>)}
        {edit && <button onClick={() => put({ day_types: times, default_when_missing: assumed }, '✓ Saved')}
          className="mt-4 px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm">Save</button>}
      </div>

      <div className={card}>
        <div className={head}>HRX's e-mails</div>
        <label className="flex items-center gap-2 text-sm text-gray-200 mb-2">
          <input type="checkbox" checked={!!data.settings.read_mail} disabled={!edit}
            onChange={e => put({ read_mail: e.target.checked }, e.target.checked ? '✓ Reading switched on' : '✓ Reading switched off')} />
          Read HRX's e-mails from {data.settings.mailbox} automatically (every 15 minutes; one search, and each new e-mail once)
        </label>
        <div className="text-xs text-gray-500 mb-3">
          {lp ? (lp.error ? <span className="text-red-400">Last read {fmtWh(lp.at, 'datetime')}: {lp.error}</span>
            : `Last read ${fmtWh(lp.at, 'datetime')} — ${lp.found} found, ${lp.new} new`)
            : data.settings.read_mail ? 'Not read since the last restart yet (the first read comes 15 minutes after a start)' : 'Reading is off'}</div>
        <div className="space-y-1 text-sm">
          {data.emails.map((e: any) => (
            <div key={e.id}>
              <div className="flex flex-wrap items-center gap-3 text-gray-300">
                <span className="w-32 text-gray-400">{fmtWh(e.received_at, 'datetime')}</span>
                <span className="flex-1 min-w-[200px]">{e.subject}</span>
                <span className={`text-xs ${e.parse_status === 'ok' ? 'text-emerald-400' : e.parse_status === 'failed' ? 'text-red-400' : 'text-amber-300'}`}>
                  {e.parse_status}{e.days ? ` · ${e.days} days` : ''}{e.weeks?.length ? ` · ${e.weeks.map((w: any) => `${dm(w.start)}–${dm(w.end)}`).join(', ')}` : ''}{e.added_by ? ` · pasted by ${e.added_by}` : ''}</span>
                <button onClick={() => show(e.id)} className="text-xs text-blue-400 hover:underline">{open?.id === e.id ? 'hide' : 'show'}</button>
              </div>
              {open?.id === e.id && <pre className="mt-1 mb-2 p-3 rounded bg-[#0f1117] border border-gray-800 text-xs text-gray-300 whitespace-pre-wrap">{open.top_text || '(no text stored)'}</pre>}
            </div>
          ))}
          {!data.emails.length && <div className="text-gray-500">None yet.</div>}
        </div>
      </div>

      {edit && (
        <div className={card}>
          <div className={head}>Paste an HRX e-mail</div>
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="subject (optional)" className={`${input} w-full mb-2`} />
          <textarea value={paste} onChange={e => setPaste(e.target.value)} rows={6} placeholder="Lugupeetud partner, … 02.11-08.11 nädal 45 …" className={`${input} w-full`} />
          <button onClick={sendPaste} disabled={paste.trim().length < 20} className="mt-2 px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm disabled:opacity-40">Read it</button>
        </div>
      )}
    </div>
  );
}
